import React, { ForwardedRef, useState } from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiLink, BiSolidBot, BiSolidChat, BiSolidMessageDetail } from 'react-icons/bi';

import { IGetAllScenariosResponse } from '@/app/services/chatbot-rtk.type';
import { BiLouise } from '@/features/appearance/AppearanceSharedAi';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

interface CustomToggleProps {
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}
export function BotDependency({ item, source }: { item: IGetAllScenariosResponse; source: IGetAllScenariosResponse[] | undefined }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  const renderVideoFloatingButton = (icon: { type: string; value: string }) => {
    const icons: Record<string, React.ReactNode> = {
      '4': <BiLouise />,
      '1': <BiSolidBot size={30} />,
      '2': <BiSolidChat size={30} />,
      '3': <BiSolidMessageDetail size={30} />,
    };

    if (icon.type === 'icon') {
      return (
        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-blue-500 tw-text-white">
          {icons[icon.value]}
        </div>
      );
    } else if (icon.type === 'image') {
      return <img src={icon.value} alt={''} className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover" />;
    } else if (icon.type === 'video') {
      return (
        <video autoPlay loop muted playsInline className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover">
          <source src={icon.value}></source>
        </video>
      );
    } else {
      return null;
    }
  };

  const CustomBotDependency = React.forwardRef<HTMLSpanElement, CustomToggleProps>(({ children, onClick, ...props }, ref) => {
    return (
      <span
        ref={ref as ForwardedRef<HTMLSpanElement>}
        role="button"
        tabIndex={0}
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent<HTMLSpanElement, MouseEvent>);
          }
        }}
        onMouseEnter={props.onMouseEnter}
      >
        <div className="tw-w-max  tw-px-4 tw-py-0.5 tw-bg-gray-400 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex  tw-h-[1rem]">
          <div className="tw-text-center tw-text-white tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
            <BiLink className="tw-cursor-pointer tw-w-5 tw-h-5" />
          </div>
        </div>
      </span>
    );
  });
  CustomBotDependency.displayName = 'CustomBotDependency';

  const hasDependsOn = item?.dependencies?.dependsOn && item?.dependencies?.dependsOn?.length > 0;
  const hasRequiredFor = item?.dependencies?.requiredFor && item?.dependencies?.requiredFor?.length > 0;

  if (hasDependsOn || hasRequiredFor) {
    return (
      <Dropdown show={show} onToggle={isOpen => setShow(isOpen)} onMouseLeave={() => setShow(false)} translate="no" className="tw-h-[28px]">
        <Dropdown.Toggle as={CustomBotDependency} onMouseEnter={() => setShow(true)} onClick={() => setShow(true)}></Dropdown.Toggle>
        <Dropdown.Menu>
          {hasDependsOn && (
            <>
              <div className="tw-gap-2 tw-ml-2 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                <small>{t('Dependencies/message')}</small>
              </div>
              <div className="mt-3">
                {item?.dependencies.dependsOn.map(id => {
                  const temp = source?.find(dr => dr.id === id);
                  if (!temp) return null;

                  return (
                    <Dropdown.Item key={id} disabled>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-relative">
                          {temp.type === BotTypeEnum.VIDEO_SCENARIO ? (
                            renderVideoFloatingButton(temp.icon)
                          ) : (
                            <img
                              className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                              src={temp.logo && temp.logo !== '' ? temp.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'}
                              alt="chatbot message"
                            />
                          )}

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {t('General/key3')} {!temp.is_default ? t('DashboardHeaderSection/deactivated') : t('DashboardHeaderSection/activated')}
                              </Tooltip>
                            }
                          >
                            <div
                              className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                                !temp.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                              } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                            />
                          </OverlayTrigger>
                        </div>
                        <div className="tw-text-left">
                          <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{temp.title}</div>
                          <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                            {Utilities.getBotNameByBotCode(temp.type, t)}
                          </div>
                        </div>
                      </div>
                    </Dropdown.Item>
                  );
                })}
              </div>
            </>
          )}
          {hasRequiredFor && (
            <>
              <div className="tw-gap-2 tw-ml-2 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                <small>{t('Dependencies/requiredFor')}</small>
              </div>
              <div className="mt-3">
                {item?.dependencies.requiredFor.map(id => {
                  const temp = source?.find(dr => dr.id === id);
                  if (!temp) return null;

                  return (
                    <Dropdown.Item key={id} disabled>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <div className="tw-relative">
                          {temp.type === BotTypeEnum.VIDEO_SCENARIO ? (
                            renderVideoFloatingButton(temp.icon)
                          ) : (
                            <img
                              className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                              src={temp.logo && temp.logo !== '' ? temp.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'}
                              alt="chatbot message"
                            />
                          )}

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {t('General/key3')} {!temp.is_default ? t('DashboardHeaderSection/deactivated') : t('DashboardHeaderSection/activated')}
                              </Tooltip>
                            }
                          >
                            <div
                              className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                                !temp.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                              } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                            />
                          </OverlayTrigger>
                        </div>
                        <div className="tw-text-left">
                          <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{temp.title}</div>
                          <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                            {Utilities.getBotNameByBotCode(temp.type, t)}
                          </div>
                        </div>
                      </div>
                    </Dropdown.Item>
                  );
                })}
              </div>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  } else {
    return <></>;
  }
}
